import { createAuthenticatedFetch, type AssetExistsResponse } from './types/core/common';
import type { ProjectWithMemos } from './types/entities/project/relations';
import type { Project, ProjectCreate } from './types/entities/project/types';

// Interface pour les erreurs API
interface ApiError {
  status?: number;
  message?: string;
}

// @deprecated Use imports from '@/api/types' instead
export type {
    Project,
    ProjectCreate,
    ProjectWithMemos
};

const api = createAuthenticatedFetch();

export const projects = {
  // ===============================
  // Méthodes Legacy (compatibilité)
  // ===============================
  
  async fetchAllProjects(): Promise<Project[]> {
    try {
      return await api.get('/projects/');
    } catch (error) {
      throw new Error("Erreur lors de la récupération des projets");
    }
  },

  // ===============================
  // Nouvelles Méthodes Sécurisées
  // ===============================

  /**
   * Récupère les projets de l'utilisateur connecté
   */
  async getMy(): Promise<Project[]> {
    return api.get<Project[]>('/projects/my');
  },

  /**
   * Récupère les projets publics seulement
   */
  async getPublic(): Promise<Project[]> {
    return api.get<Project[]>('/projects/public');
  },

  /**
   * Récupère les projets accessibles (utilisateur + publics)
   */
  async getAccessible(): Promise<Project[]> {
    return api.get<Project[]>('/projects/accessible');
  },

  /**
   * Récupère tous les projets (admin seulement)
   */
  async getAllAdmin(): Promise<Project[]> {
    return api.get<Project[]>('/projects/all');
  },

  /**
   * Récupère tous les projets (admin seulement), y compris soft-deleted
   */
  async getAllAdminWithDeleted(): Promise<Project[]> {
    return api.get<Project[]>('/projects/all_with_deleted');
  },

  // ===============================
  // Méthodes CRUD Sécurisées
  // ===============================

  async createProject(project: ProjectCreate): Promise<Project> {
    return api.post<Project>('/projects/', project);
  },

  async updateProject(uuid: string, project: Partial<ProjectCreate>): Promise<Project> {
    return api.put<Project>(`/projects/${uuid}`, project);
  },

  async deleteProject(uuid: string): Promise<void> {
    return api.delete(`/projects/${uuid}`);
  },

  async hardDeleteProject(uuid: string): Promise<void> {
    return api.delete(`/projects/hard_delete/${uuid}`);
  },
  async restoreProject(uuid: string): Promise<void> {
    return api.put(`/projects/restore/${uuid}`);
  },

  async fetchProjectById(uuid: string): Promise<Project> {
    try {
      return await api.get(`/projects/${uuid}`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Projet non trouvé");
      }
      throw new Error("Erreur lors de la récupération du projet");
    }
  },

  async fetchProjectsByUserId(userId: string): Promise<Project[]> {
    try {
      return await api.get(`/projects/user/${userId}`);
    } catch (error) {
      throw new Error("Erreur lors de la récupération des projets de l'utilisateur");
    }
  },

  async fetchAllProjectsAdmin(): Promise<Project[]> {
    try {
      return await api.get('/projects/admin/all');
    } catch (error) {
      throw new Error("Erreur lors de la récupération de tous les projets (admin)");
    }
  },

  async fetchProjectWithMemos(uuid: string): Promise<ProjectWithMemos> {
    try {
      return await api.get(`/projects/${uuid}/with-memos`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Projet non trouvé");
      }
      throw new Error("Erreur lors de la récupération du projet et ses mémos");
    }
  },

  /**
   * Récupère un projet public par UUID (accès public)
   */
  async getPublicByUuid(uuid: string): Promise<Project> {
    try {
      return await api.get(`/projects/public/${uuid}`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Projet public non trouvé");
      }
      throw new Error("Erreur lors de la récupération du projet public");
    }
  },

  /**
   * Récupère un projet par UUID avec ses mémos (accès public ou par connaissance de l'UUID)
   */
  async getPublicByUuidWithMemos(uuid: string): Promise<ProjectWithMemos> {
    try {
      return await api.get(`/projects/public/${uuid}/with-memos`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Projet non trouvé");
      }
      throw new Error("Erreur lors de la récupération du projet et ses mémos");
    }
  },

  /**
   * Vérifie si un projet existe par son UUID (public ou privé)
   */
  async checkExists(uuid: string): Promise<AssetExistsResponse> {
    try {
      return await api.get(`/projects/exists/${uuid}`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        return {
          exists: false,
          is_owner: false,
          is_public: false
        };
      }
      throw new Error("Erreur lors de la vérification de l'existence du projet");
    }
  }
};

// Exports des fonctions individuelles pour la rétrocompatibilité
export const fetchAllProjects = (): Promise<Project[]> => projects.fetchAllProjects();
export const createProject = (project: ProjectCreate): Promise<Project> => projects.createProject(project);
export const updateProject = (uuid: string, project: Partial<ProjectCreate>): Promise<Project> => projects.updateProject(uuid, project);
export const deleteProject = (uuid: string): Promise<void> => projects.deleteProject(uuid);
export const fetchProjectById = (uuid: string): Promise<Project> => projects.fetchProjectById(uuid);
export const fetchProjectsByUserId = (userId: string): Promise<Project[]> => projects.fetchProjectsByUserId(userId);
export const fetchAllProjectsAdmin = (): Promise<Project[]> => projects.fetchAllProjectsAdmin();
export const fetchAllAdminWithDeleted = (): Promise<Project[]> => projects.getAllAdminWithDeleted();
export const fetchProjectWithMemos = (uuid: string): Promise<ProjectWithMemos> => projects.fetchProjectWithMemos(uuid); 